import React from 'react';
import { AsyncPaginate as ReactSelectAsyncPaginate } from 'react-select-async-paginate';

/**
 * Reusable AsyncPaginate component wrapper around react-select-async-paginate
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Name attribute for the select
 * @param {Object|null} props.value - Selected value/option
 * @param {Function} props.onChange - Callback when value changes
 * @param {Function} props.loadOptions - Function to load options asynchronously
 * @param {Function} props.getOptionLabel - Function to get label from option
 * @param {Function} props.getOptionValue - Function to get value from option
 * @param {Object} props.additional - Additional pagination config (pageLimit, pagination_token, etc.)
 * @param {number} props.debounceTimeout - Debounce timeout in ms (default: 300)
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {string} props.className - CSS class names
 * @param {boolean} props.isClearable - Whether the select is clearable
 * @param {boolean} props.cacheOptions - Whether to cache options
 * @param {boolean} props.filter - Whether to show filter
 * @param {string} props.filterPlaceholder - Placeholder for filter input
 * @param {Array} props.defaultOptions - Default options to show
 * @param {Object} props.rest - Any other props to pass to the underlying AsyncPaginate
 */
const AsyncPaginate = ({
  name,
  value,
  onChange,
  loadOptions,
  getOptionLabel,
  getOptionValue,
  additional = {
    pageLimit: 15,
    pagination_token: '',
    pagination_direction: 'forward',
    sort_order: 'alphabet_ascending',
  },
  debounceTimeout = 300,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  isClearable = false,
  defaultOptions,
  cacheUniqs,
  styles,
  ...rest
}) => {
  const getAsyncPaginateStyles = (styles) => {
    const isDarkMode = styles === 'dark';
    return {
      control: (base) => ({
        ...base,
        backgroundColor: 'transparent',
        borderColor: isDarkMode ? '#1e293b' : '#d1d5db',
        boxShadow: 'none',
        borderRadius: '8px',
      }),

      menu: (base) => ({
        ...base,
        backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
        zIndex: 9999,
      }),

      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? isDarkMode
            ? 'rgba(37, 99, 235, 0.85)'
            : 'rgba(37, 99, 235, 0.15)'
          : state.isFocused
          ? isDarkMode
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.05)'
          : 'transparent',
        color: isDarkMode ? '#e5e7eb' : '#48515b',
      }),

      singleValue: (base) => ({
        ...base,
        color: isDarkMode ? '#e5e7eb' : '#48515b',
      }),

      placeholder: (base) => ({
        ...base,
        color: isDarkMode ? '#94a3b8' : '#48515b',
      }),
    };
  };

  return (
    <ReactSelectAsyncPaginate
      name={name}
      value={value}
      onChange={onChange}
      loadOptions={loadOptions}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      additional={additional}
      debounceTimeout={debounceTimeout}
      placeholder={placeholder}
      isDisabled={disabled}
      className={className}
      isClearable={isClearable}
      defaultOptions={defaultOptions}
      cacheUniqs={cacheUniqs}
      styles={getAsyncPaginateStyles(styles)}
      {...rest}
    />
  );
};

export default AsyncPaginate;
